/**
 * MED-1349 follow-up: the Cohort Events header summary and the row's
 * "Attendance Pending" badge.
 */
import type { CohortEventWithDetails } from '../../../../services/api/cohortEvents';

export interface CohortEventSummary {
  completed: number;
  awaiting: number;
  upcoming: number;
}

/** The event has ended (its end time, or its start when it has no end, is before now). */
export function isCohortEventPast(event: CohortEventWithDetails, now: Date): boolean {
  const endTime = event.scheduled_end || event.scheduled_start;
  if (!endTime) return false;
  return new Date(endTime) < now;
}

/**
 * Each event counts once. A completed event is completed. An event that is not
 * completed and has ended, or has the status awaiting_attendance, is awaiting
 * attendance. Every other event, including one with no date, is upcoming.
 */
export function summarizeCohortEvents(
  events: CohortEventWithDetails[],
  now: Date,
): CohortEventSummary {
  const summary: CohortEventSummary = { completed: 0, awaiting: 0, upcoming: 0 };
  for (const event of events) {
    if (event.status === 'completed') summary.completed++;
    else if (event.status === 'awaiting_attendance' || isCohortEventPast(event, now))
      summary.awaiting++;
    else summary.upcoming++;
  }
  return summary;
}

/**
 * The badge shows for an ended event with no attendance, unless the status
 * badge already says Awaiting Attendance.
 */
export function showsAttendancePendingBadge(event: CohortEventWithDetails, now: Date): boolean {
  return (
    event.status !== 'awaiting_attendance' &&
    isCohortEventPast(event, now) &&
    !event.attendance_marked
  );
}
